"use client";

import type React from "react";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
  VStack,
  Accordion,
  useToast,
  Spinner,
  Divider,
  Input,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ArrowLeft, Save, Copy, RotateCcw, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  whatsappAgentService,
  type WhatsAppAgent,
} from "../../whatsapp-services/agentService";
import { WhatsAppModelSelect } from "./WhatsAppModelSelect";
import { WhatsAppLanguageSelect } from "./WhatsAppLanguageSelect";
import { WhatsAppAgentFunctions } from "./WhatsAppAgentFunctions";
import { WhatsAppChatSettings } from "./WhatsAppChatSettings";
import { WhatsAppAgentKnowledgeBase } from "./WhatsAppAgentKnowledgeBase";
import { DynamicVariablesButton } from "./DynamicVariablesButton";
import { EditableWhatsAppAgentName } from "./EditableWhatsAppAgentName";

const wiggle = keyframes`
  0% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.08) rotate(-6deg); }
  50% { transform: scale(1.1) rotate(6deg); }
  75% { transform: scale(1.08) rotate(-4deg); }
  100% { transform: scale(1.06) rotate(0deg); }
`;

interface WhatsAppAgentConfigProps {
  agent: WhatsAppAgent;
}

interface Message {
  type: "human" | "ai";
  content: string;
}

function getShortId(id: string) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

function generateConversationId(): string {
  const randomStr =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  return `web-${randomStr}`;
}

const WhatsAppAgentConfig: React.FC<WhatsAppAgentConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();

  const [agent, setAgent] = useState<WhatsAppAgent>(initialAgent);
  const [prompt, setPrompt] = useState(agent.prompt || "");
  const [initialMessage, setInitialMessage] = useState(
    agent.initial_message || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});

  const [isChatMode, setIsChatMode] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [senderId, setSenderId] = useState<string>(() =>
    generateConversationId()
  );
  const [receiverId, setReceiverId] = useState<string>(() =>
    generateConversationId()
  );

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const scrollbarStyles = {
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "gray.300",
      borderRadius: "6px",
      border: "2px solid transparent",
    },
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await whatsappAgentService.update(agent.agent_id, {
        prompt,
        initial_message: initialMessage,
      });
      toast({
        title: "Agent saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving agent",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInitialMessageBlur = async () => {
    try {
      await whatsappAgentService.update(agent.agent_id, {
        initial_message: initialMessage,
      });
      toast({
        title: "Initial message saved",
        status: "success",
        duration: 2000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving initial message",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) console.log("Variables updated:", vars);
      setDynamicVariables(vars);
    },
    []
  );

  const handleStartChat = () => {
    setIsChatMode(true);
    if (initialMessage) {
      setMessages([
        {
          type: "ai",
          content: initialMessage,
        },
      ]);
    }
  };

  const handleResetConversation = () => {
    setMessages([]);
    setUserInput("");
    setIsChatMode(false);
    setSenderId(generateConversationId());
    setReceiverId(generateConversationId());
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const userMessage = userInput.trim();
    setUserInput("");

    const newMessages: Message[] = [
      ...messages,
      {
        type: "human",
        content: userMessage,
      },
    ];
    setMessages(newMessages);
    setIsSendingMessage(true);

    try {
      const formattedDynamicVars: Record<string, string> = {};
      Object.keys(dynamicVariables).forEach((key) => {
        const value = dynamicVariables[key];
        formattedDynamicVars[key] = value.trim() === "" ? `<<${key}>>` : value;
      });

      const response = await fetch(
        `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:8000"
        }/api/whatsapp/ask-agent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agent_id: agent.agent_id,
            user_message: userMessage,
            dynamic_vars_values: formattedDynamicVars,
            sender_id: senderId,
            receiver_id: receiverId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from agent");
      }

      const data = await response.json();
      const agentResponse =
        data.agent_response || data.response || "No response";

      setMessages([
        ...newMessages,
        {
          type: "ai",
          content: agentResponse,
        },
      ]);
    } catch (error: any) {
      console.error("[v0] Error sending message:", error);
      toast({
        title: "Error sending message",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const testPanel = useMemo(
    () => (
      <VStack
        w="300px"
        p={4}
        borderLeft="1px solid"
        borderColor="gray.200"
        spacing={4}
      >
        <Flex align="center" justify="space-between" w="full" bg="gray.100">
          <Text px={3} py={1} rounded="md" fontWeight="semibold">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt={prompt}
            beginMessage={initialMessage}
          />
        </Flex>

        <Divider />

        {!isChatMode ? (
          <Box textAlign="center" py={8}>
            <Box as="span" fontSize="48px">
              <IconButton
                w="full"
                background="transparent"
                _hover={{ background: "transparent" }}
                aria-label="Start Chat"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="68"
                    height="68"
                    viewBox="0 0 48 48"
                  >
                    <path
                      fill="#fff"
                      d="M4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5c5.1,0,9.8,2,13.4,5.6 C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19c0,0,0,0,0,0h0c-3.2,0-6.3-0.8-9.1-2.3L4.9,43.3z"
                    ></path>
                    <path
                      fill="#fff"
                      d="M4.9,43.8c-0.1,0-0.3-0.1-0.4-0.1c-0.1-0.1-0.2-0.3-0.1-0.5L7,33.5c-1.6-2.9-2.5-6.2-2.5-9.6 C4.5,13.2,13.3,4.5,24,4.5c5.2,0,10.1,2,13.8,5.7c3.7,3.7,5.7,8.6,5.7,13.8c0,10.7-8.7,19.5-19.5,19.5c-3.2,0-6.3-0.8-9.1-2.3 L5,43.8C5,43.8,4.9,43.8,4.9,43.8z"
                    ></path>
                    <path
                      fill="#cfd8dc"
                      d="M24,5c5.1,0,9.8,2,13.4,5.6C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19h0c-3.2,0-6.3-0.8-9.1-2.3 L4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5 M24,43L24,43L24,43 M24,43L24,43L24,43 M24,4L24,4C13,4,4,13,4,24 c0,3.4,0.8,6.7,2.5,9.6L3.9,43c-0.1,0.3,0,0.7,0.3,1c0.2,0.2,0.4,0.3,0.7,0.3c0.1,0,0.2,0,0.3,0l9.7-2.5c2.8,1.5,6,2.2,9.2,2.2 c11,0,20-9,20-20c0-5.3-2.1-10.4-5.8-14.1C34.4,6.1,29.4,4,24,4L24,4z"
                    ></path>
                    <path
                      fill="#40c351"
                      d="M35.2,12.8c-3-3-6.9-4.6-11.2-4.6C15.3,8.2,8.2,15.3,8.2,24c0,3,0.8,5.9,2.4,8.4L11,33l-1.6,5.8 l6-1.6l0.6,0.3c2.4,1.4,5.2,2.2,8,2.2h0c8.7,0,15.8-7.1,15.8-15.8C39.8,19.8,38.2,15.8,35.2,12.8z"
                    ></path>
                    <path
                      fill="#fff"
                      fillRule="evenodd"
                      d="M19.3,16c-0.4-0.8-0.7-0.8-1.1-0.8c-0.3,0-0.6,0-0.9,0 s-0.8,0.1-1.3,0.6c-0.4,0.5-1.7,1.6-1.7,4s1.7,4.6,1.9,4.9s3.3,5.3,8.1,7.2c4,1.6,4.8,1.3,5.7,1.2c0.9-0.1,2.8-1.1,3.2-2.3 c0.4-1.1,0.4-2.1,0.3-2.3c-0.1-0.2-0.4-0.3-0.9-0.6s-2.8-1.4-3.2-1.5c-0.4-0.2-0.8-0.2-1.1,0.2c-0.3,0.5-1.2,1.5-1.5,1.9 c-0.3,0.3-0.6,0.4-1,0.1c-0.5-0.2-2-0.7-3.8-2.4c-1.4-1.3-2.4-2.8-2.6-3.3c-0.3-0.5,0-0.7,0.2-1c0.2-0.2,0.5-0.6,0.7-0.8 c0.2-0.3,0.3-0.5,0.5-0.8c0.2-0.3,0.1-0.6,0-0.8C20.6,19.3,19.7,17,19.3,16z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                }
                sx={{
                  // target the svg inside the button
                  "& svg": {
                    transition: "transform 0.2s ease, filter 0.2s ease",
                  },
                  "&:hover svg": {
                    animation: `${wiggle} 0.45s ease-in-out`,
                    filter: "drop-shadow(0 0 6px rgba(64,195,81,0.9))",
                  },
                }}
              />
            </Box>

            <Button
              mt={4}
              w="full"
              colorScheme="green"
              bg="green.500"
              _hover={{ bg: "green.600" }}
              variant="solid"
              onClick={handleStartChat}
            >
              Start Chat
            </Button>
          </Box>
        ) : (
          <VStack w="full" h="calc(100vh - 200px)" spacing={0}>
            <Box
              position="relative"
              w="full"
              flex="1"
              display="flex"
              flexDirection="column"
              minH="0"
            >
              <IconButton
                aria-label="Reset conversation"
                icon={<RotateCcw size={16} />}
                size="sm"
                variant="ghost"
                onClick={handleResetConversation}
                title="Reset conversation"
                position="absolute"
                top="2"
                right="2"
                zIndex={2}
                bg="whiteAlpha.800"
                _hover={{ bg: "whiteAlpha.900" }}
                boxShadow="sm"
                borderRadius="full"
              />

              <VStack
                flex={1}
                w="full"
                overflowY="auto"
                spacing={3}
                align="stretch"
                css={scrollbarStyles}
                p={2}
                bg="gray.50"
                borderRadius="md"
                minH="0"
              >
                {messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    alignSelf={msg.type === "human" ? "flex-end" : "flex-start"}
                    maxW="85%"
                  >
                    <Box
                      bg={msg.type === "human" ? "green.500" : "white"}
                      color={msg.type === "human" ? "white" : "gray.800"}
                      px={3}
                      py={2}
                      borderRadius="lg"
                      fontSize="sm"
                      boxShadow="sm"
                      border={msg.type === "ai" ? "1px solid" : "none"}
                      borderColor="gray.200"
                    >
                      {msg.content}
                    </Box>
                  </Box>
                ))}

                {isSendingMessage && (
                  <Box alignSelf="flex-start" maxW="85%">
                    <Box
                      bg="white"
                      color="gray.800"
                      px={3}
                      py={2}
                      borderRadius="lg"
                      fontSize="sm"
                      boxShadow="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <style>
                        {`
                        @keyframes typing-dot {
                          0%, 60%, 100% { opacity: 0.3; }
                          30% { opacity: 1; }
                        }
                      `}
                      </style>
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0s" }}
                      />
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0.2s" }}
                      />
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0.4s" }}
                      />
                    </Box>
                  </Box>
                )}

                <div ref={chatEndRef} />
              </VStack>
            </Box>

            <Flex w="full" mt={3} gap={2}>
              <Input
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                size="sm"
                disabled={isSendingMessage}
                bg="white"
              />
              <IconButton
                aria-label="Send message"
                icon={<Send size={16} />}
                size="sm"
                colorScheme="green"
                onClick={handleSendMessage}
                isLoading={isSendingMessage}
                disabled={!userInput.trim() || isSendingMessage}
              />
            </Flex>
          </VStack>
        )}
      </VStack>
    ),
    [
      handleDynamicVariablesChange,
      prompt,
      initialMessage,
      isChatMode,
      messages,
      userInput,
      isSendingMessage,
      scrollbarStyles,
    ]
  );

  const mainContent = useMemo(
    () => (
      <Flex h="100vh" bg="white" color="gray.800" direction="column">
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center"
        >
          <IconButton
            aria-label="Back"
            icon={<ArrowLeft />}
            variant="ghost"
            color="gray.800"
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate("/whatsapp-agents")}
          />
          <Box ml={4}>
            <EditableWhatsAppAgentName
              agentId={agent.agent_id}
              initialName={agent.name || ""}
            />
            <Text color="gray.500" fontSize="sm">
              WhatsApp &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
              <IconButton
                aria-label="Copy Agent ID"
                icon={<Copy size={12} />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(agent.agent_id)}
                color="gray.500"
              />
            </Text>
          </Box>
        </Flex>

        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          gap={2}
        >
          <WhatsAppModelSelect
            agentId={agent.agent_id}
            initialModel={agent.llm || "gpt-4o"}
            initialTemperature={agent.llm_temperature}
            onModelUpdate={(newModel) => setAgent({ ...agent, llm: newModel })}
          />
          <WhatsAppLanguageSelect
            agentId={agent.agent_id}
            initialLanguage={agent.language || "en-US"}
            onLanguageUpdate={(newLang) =>
              setAgent({ ...agent, language: newLang })
            }
          />
        </Flex>

        <Flex flex={1} overflow="hidden" alignItems="stretch">
          <Box
            flex={1}
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            maxW="calc(100% - 600px)"
            minW="500px"
          >
            <VStack
              align="stretch"
              h="full"
              spacing={4}
              overflowY="auto"
              css={scrollbarStyles}
            >
              <Box position="relative" flex={0} minH="400px">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "green.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-green-500)",
                  }}
                  resize="none"
                  fontSize="sm"
                  placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                  pr="100px"
                  height="100%"
                  onMouseEnter={() => setNoteVisible(true)}
                  onMouseLeave={() => setNoteVisible(false)}
                  isReadOnly={!isAdmin}
                />
                {!isAdmin && (
                  <Text fontSize="xs" color="orange.400" mt={1}>Prompt editing is restricted to administrators.</Text>
                )}
                <Button
                  leftIcon={<Save size={16} />}
                  onClick={handleSave}
                  isLoading={isSaving}
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  size="sm"
                  colorScheme="green"
                  bg="green.500"
                  _hover={{ bg: "green.600" }}
                  zIndex={2}
                >
                  Save
                </Button>
                {noteVisible && (
                  <Text
                    position="absolute"
                    bottom="8px"
                    left="8px"
                    fontSize="xs"
                    color="gray.500"
                  >
                    Use `{"{{_}}"}` to add variables.
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Initial Message
                </Text>
                <Input
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  onBlur={handleInitialMessageBlur}
                  placeholder="Enter the initial message to send when chat starts"
                  size="md"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "green.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-green-500)",
                  }}
                />
              </Box>
            </VStack>
          </Box>

          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            css={scrollbarStyles}
          >
            <Accordion allowMultiple w="full">
              {isAdmin && (
                <WhatsAppAgentFunctions
                  agentId={agent.agent_id}
                  initialFunctions={
                    agent.tools?.map((tool) => {
                      if (typeof tool === "string") {
                        try {
                          let parsed = JSON.parse(tool);
                          if (typeof parsed === "string") {
                            parsed = JSON.parse(parsed);
                          }
                          if (parsed && typeof parsed === "object") {
                            if (
                              typeof parsed.type === "string" &&
                              parsed.type.startsWith("{")
                            ) {
                              try {
                                parsed = JSON.parse(parsed.type);
                              } catch {
                                // Keep original parsed object
                              }
                            }
                          }
                          return parsed;
                        } catch (e) {
                          console.error("[v0] Error parsing tool:", tool, e);
                          return { type: tool, name: tool };
                        }
                      }
                      return tool;
                    }) || []
                  }
                />
              )}

              <WhatsAppAgentKnowledgeBase agentId={agent.agent_id} />

              <WhatsAppChatSettings
                agentId={agent.agent_id}
                initialMaxChatLength={agent.max_chat_length || 50}
                initialTone={agent.tone || "professional"}
              />
            </Accordion>
          </VStack>

          {testPanel}
        </Flex>
      </Flex>
    ),
    [agent, prompt, initialMessage, noteVisible, navigate, testPanel, isSaving]
  );

  return isLoading ? (
    <Flex h="100vh" justify="center" align="center" bg="white">
      <Spinner size="xl" color="green.500" />
    </Flex>
  ) : (
    mainContent
  );
};

export default WhatsAppAgentConfig;
